// ブラウザテスト (Playwright) から内部を操作するためのフック。
// URL に ?test=1 が付いたときだけ window.__bpe を公開する。

import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore } from '@/stores/uiStore'
import { pixelStore } from '@/pixels/pixelStore'
import { exportPixels, beginStroke, addStrokeSample, endStroke, frameStats, compositorStats, requestFrame, loseContextForTest } from './engine'
import { compositePixel, to8 } from '@/render/blendModes'
import { surfaceFromRgba } from '@/pixels/imageDataUtil'
import { createRasterLayer, newLayerId, newPixelId } from '@/model/defaults'
import * as actions from './actions'
import { createProjectBlob, loadProjectBlob } from '@/io/projectZip'
import type { BlendMode } from '@/types/document'
import { runCommand, listCommands } from '@/commands/registry'
import { useActionsStore } from '@/automation/actions'
import { useBrushStore } from '@/brush/presets'
import { describeDocument, previewPng } from '@/commands/describe'
import { applyAdjustmentCpu } from '@/render/adjust/lut'
import { connectBridge, disconnectBridge, bridgeState, setBridgePort } from '@/mcp/bridgeClient'
import { ensureFont, isFontAvailable, usedUserFonts, fontEmbedWarnings } from '@/text/fonts'

export function installTestHooks(): void {
  if (!new URLSearchParams(location.search).has('test')) return
  const api = {
    useDocumentStore,
    useUiStore,
    pixelStore,
    exportPixels,
    compositePixel,
    to8,
    actions,
    createProjectBlob,
    loadProjectBlob,
    runCommand,
    useActionsStore,
    useBrushStore,
    listCommands,
    frameStats,
    compositorStats,
    requestFrame,
    loseContextForTest,
    applyAdjustmentCpu,
    connectBridge,
    disconnectBridge,
    bridgeState,
    setBridgePort,
    describeDocument,
    previewPng,
    ensureFont,
    isFontAvailable,
    usedUserFonts,
    fontEmbedWarnings,
    /** プログラムからストロークを描く (テスト用) */
    stroke(points: { x: number; y: number; pressure?: number }[], opts: Partial<import('@/brush/brushEngine').StrokeParams> & { layerId: string }) {
      const params: import('@/brush/brushEngine').StrokeParams = {
        color: [0, 0, 0], opacity: 1, flow: 1, size: 10, hardness: 1, spacing: 0.1, smoothing: 0, mode: 'paint', pencil: false,
        pressureSize: false, pressureFlow: false, lockTransparency: false, ...opts
      }
      if (!beginStroke(params)) throw new Error('beginStroke failed')
      for (const p of points) addStrokeSample({ x: p.x, y: p.y, pressure: p.pressure ?? 1 })
      const last = points[points.length - 1]!
      endStroke({ x: last.x, y: last.y, pressure: last.pressure ?? 1 }, 'history.brush')
    },
    /** 指定の RGBA 配列を持つラスターレイヤーを追加する */
    addLayer(name: string, w: number, h: number, rgba: number[], opts: { blendMode?: BlendMode; opacity?: number; offset?: { x: number; y: number } } = {}) {
      const store = useDocumentStore()
      const id = newLayerId()
      store.mutate('history.addLayer', (draft, px) => {
        const s = surfaceFromRgba(newPixelId(), w, h, new Uint8ClampedArray(rgba))
        px.adopt(s)
        const layer = createRasterLayer({ id, name, pixelId: s.id, blendMode: opts.blendMode ?? 'normal', opacity: opts.opacity ?? 1, offset: opts.offset ?? { x: 0, y: 0 } })
        draft.layers[id] = layer
        draft.rootChildren.push(id)
        draft.activeLayerIds = [id]
      })
      return id
    }
  }
  ;(window as unknown as { __bpe: typeof api }).__bpe = api
}
